import React from 'react';
import { useSelector } from 'react-redux';
import { selectDrone } from '@src/features/fleet/pages/drones-page/redux';
import { DroneEntity } from '@src/core/features';
import { useGetDroneHistoryQuery, useGetDroneMaintenanceQuery, useAdjustDroneMaintenanceMutation, useResetPartDroneMutation } from '@src/features/fleet/redux/api';
import {
  ArcButton,
  ArcDialog,
  ArcIconButton,
  ArcMenuItem,
  ArcOutlinedInput,
  ArcProgressBar,
  ArcStack,
  ArcText,
  ArcTooltip,
} from '@src/core/ui';
import { InputAdornment, Menu } from '@mui/material';
import { ReactComponent as ReplaceIcon } from '../../../../../../assets/icons/replace.svg';
import { ReactComponent as AdjustIcon } from '../../../../../../assets/icons/adjust.svg';
import { useConfirm } from '@src/core/controllers';
import { formatFlightTime, formatFlightDistance } from '@src/core/extensions';
import type { IDroneMaintenancePart, DroneMaintenancePartId } from '@src/features/fleet/redux/types';
import { ADJUST_TYPE_TO_API, AdjustPopupProps, AdjustType, RuntimeItem } from './types';

const CARD_SX = {
  border: '1px solid',
  borderColor: 'ghost.extraLight',
  borderRadius: '8px',
  padding: 3,
  flex: 1,
} as const;

interface StatCardProps {
  label: string;
  value: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ label, value }) => (
  <ArcStack spacing={1} sx={CARD_SX}>
    <ArcText.caption fontSize={16}>{label}</ArcText.caption>
    <ArcText.h2 fontSize={24} fontWeight={700}>{value}</ArcText.h2>
  </ArcStack>
);

interface RuntimeItemRowProps extends RuntimeItem {
  onAdjustOptionClick?: (optionId: AdjustType, item: RuntimeItem) => void;
  onReplaceClick?: (item: RuntimeItem) => void;
}

const ADJUST_OPTIONS = [
  { id: 'runtime', label: 'Adjust Runtime' },
  { id: 'serviceLimit', label: 'Adjust Service Limit' },
] as const;


const AdjustPopup: React.FC<AdjustPopupProps> = ({ type, value, open, onClose, onSave, onValueChange }) => {
  const label = type === 'runtime' ? 'Adjust Runtime' : 'Adjust Service Limit';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '' || /^\d*\.?\d?$/.test(raw)) {
      const num = raw === '' ? 0 : Number(raw);
      onValueChange(Number.isNaN(num) ? 0 : num);
    }
  };
  return (
    <ArcDialog
      open={open}
      onClose={onClose}
      title={type === 'runtime' ? 'Adjust Runtime' : 'Adjust Service Limit'}
      contextSX={{ px: 4, paddingBottom: 3 }}
    >
      <ArcStack spacing={2}>
        <ArcOutlinedInput
          autoComplete="off"
          label={label}
          value={value}
          onChange={handleChange}
          inputProps={{
            pattern: "^\\d*(\\.\\d?)?$",
            inputMode: "decimal",
          }
          }
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <ArcText.h5>h</ArcText.h5>
                </InputAdornment>
              ),
            },
          }}
        />
        <ArcButton.brand onClick={() => onSave(value)}>
          Save
        </ArcButton.brand>
      </ArcStack>
    </ArcDialog>
  );
};

const RuntimeItemRow: React.FC<RuntimeItemRowProps> = ({
  name,
  hoursLabel,
  progressValue,
  type,
  onAdjustOptionClick,
  onReplaceClick,
}) => {
  const [adjustAnchorEl, setAdjustAnchorEl] = React.useState<null | HTMLElement>(null);
  const adjustOpen = Boolean(adjustAnchorEl);
  const item: RuntimeItem = { name, hoursLabel, progressValue, type };

  const { confirm } = useConfirm();
  const handleReplace = () => {
    confirm(
      `Confirm that ${name} has been replaced. This action cannot be undone.`,
      'Confirm Replacement'
    ).then((result) => {
      if (result) onReplaceClick?.(item);
    });
  };
  const handleAdjustClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAdjustAnchorEl(event.currentTarget);
  };
  const handleAdjustClose = () => {
    setAdjustAnchorEl(null);
  };
  const handleAdjustOption = (optionId: string) => {
    handleAdjustClose();
    onAdjustOptionClick?.(optionId as AdjustType, item);
  };

  return (
    <ArcStack marginTop={2} direction="row" justifyContent="space-between" gap={1}>
      <ArcStack flex={1}>
        <ArcStack direction="row" alignItems="center" justifyContent="space-between">
          <ArcText color="white" fontSize={12}>{name}</ArcText>
          <ArcText color="white" fontSize={12}>{hoursLabel}</ArcText>
        </ArcStack>
        <ArcProgressBar.condition value={progressValue} sx={{ marginTop: 1 }} />
      </ArcStack>
      <ArcStack direction="row">
        <ArcTooltip title="Replacement">
          <ArcIconButton onClick={handleReplace}>
            <ReplaceIcon />
          </ArcIconButton>
        </ArcTooltip>
        <ArcTooltip title="Adjustment">
          <ArcIconButton onClick={handleAdjustClick} aria-controls={adjustOpen ? 'adjust-menu' : undefined} aria-haspopup="true" aria-expanded={adjustOpen ? 'true' : undefined}>
            <AdjustIcon />
          </ArcIconButton>
        </ArcTooltip>
        <Menu
          id="adjust-menu"
          anchorEl={adjustAnchorEl}
          open={adjustOpen}
          onClose={handleAdjustClose}
          MenuListProps={{ 'aria-labelledby': 'adjust-button' }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: { backgroundColor: 'white', minWidth: 160 },
            },
          }}
        >
          {ADJUST_OPTIONS.map((option) => (
            <ArcMenuItem
              key={option.id}
              onClick={() => handleAdjustOption(option.id)}
              sx={{ color: 'black', fontSize: '14px' }}
            >
              {option.label}
            </ArcMenuItem>
          ))}
        </Menu>
      </ArcStack>
    </ArcStack>
  );
};

interface RuntimeSectionProps {
  title: string;
  items: RuntimeItem[];
  onAdjustOptionClick?: (optionId: AdjustType, item: RuntimeItem) => void;
  onReplaceClick?: (item: RuntimeItem) => void;
}

const RuntimeSection: React.FC<RuntimeSectionProps> = ({ title, items, onAdjustOptionClick, onReplaceClick }) => (
  <ArcStack gap={2} spacing={1} sx={CARD_SX}>
    <ArcText.caption fontSize={16}>{title}</ArcText.caption>
    {items.map((item) => (
      <RuntimeItemRow
        key={item.name}
        type={item.type}
        name={item.name}
        hoursLabel={item.hoursLabel}
        progressValue={item.progressValue}
        onAdjustOptionClick={onAdjustOptionClick}
        onReplaceClick={onReplaceClick}
      />
    ))}
  </ArcStack>
);

const SECONDS_PER_HOUR = 3600;

function partToRuntimeItem(
  name: string,
  part: IDroneMaintenancePart,
  type: RuntimeItem['type']
): RuntimeItem {
  const totalHours = part.totalHour / SECONDS_PER_HOUR;
  const limitHours = part.limitHour / SECONDS_PER_HOUR;
  const progressValue = limitHours > 0 ? Math.min(100, Math.round((totalHours / limitHours) * 100)) : 0;
  const hoursLabel = `${totalHours.toFixed(1)}h / ${limitHours.toFixed(1)}h`;
  return { name, hoursLabel, progressValue, type };
}

function parseCurrentHours(hoursLabel: string): number {
  const match = hoursLabel.match(/^([\d.]+)/);
  return match ? Number(match[1]) : 0;
}

function parseLimitHours(hoursLabel: string): number {
  const match = hoursLabel.match(/\/\s*(\d+)/);
  return match ? Number(match[1]) : 0;
}

function runtimeItemToPartId(item: RuntimeItem): DroneMaintenancePartId {
  const num = item.name.replace(/\D/g, '') || '1';
  const prefix = item.type === 'motor' ? 'MOTOR' : 'PROP';
  return `${prefix}${num}` as DroneMaintenancePartId;
}

const MOTOR_KEYS = ['motor1', 'motor2', 'motor3', 'motor4', 'motor5', 'motor6'] as const;
const PROP_KEYS = ['prop1', 'prop2', 'prop3', 'prop4', 'prop5', 'prop6'] as const;

const MaintenancePanel: React.FC = () => {
  const selectedDrone = useSelector(selectDrone) ?? ({} as DroneEntity);
  const [open, setOpen] = React.useState(false);
  const [adjustType, setAdjustType] = React.useState<AdjustType | null>(null);
  const [adjustValue, setAdjustValue] = React.useState<number>(0);
  const [adjustingPart, setAdjustingPart] = React.useState<DroneMaintenancePartId | null>(null);

  const hasDrone = selectedDrone?.id != null;
  const { data: maintenance } = useGetDroneMaintenanceQuery(
    selectedDrone.id as number,
    { skip: !hasDrone }
  );
  const [adjustDroneMaintenance] = useAdjustDroneMaintenanceMutation();
  const [resetPartDrone] = useResetPartDroneMutation();

  const onClose = () => {
    setOpen(false);
    setAdjustType(null);
    setAdjustingPart(null);
  };

  const handleAdjustOptionClick = React.useCallback((optionId: AdjustType, item: RuntimeItem) => {
    setAdjustType(optionId);
    const value = optionId === 'runtime' ? parseCurrentHours(item.hoursLabel) : parseLimitHours(item.hoursLabel);
    setAdjustValue(value);
    setAdjustingPart(runtimeItemToPartId(item));
    setOpen(true);
  }, []);

  const handleReplaceClick = React.useCallback(
    (item: RuntimeItem) => {
      if (!hasDrone) return;
      resetPartDrone({ id: selectedDrone.id as number, part: runtimeItemToPartId(item) });
    },
    [hasDrone, selectedDrone.id, resetPartDrone]
  );

  const handleSave = React.useCallback(
    async (valueHours: number) => {
      if (adjustType == null || adjustingPart == null || !hasDrone) return;
      const type = ADJUST_TYPE_TO_API[adjustType];
      const valueSeconds = Math.round(valueHours * SECONDS_PER_HOUR);
      await adjustDroneMaintenance({
        id: selectedDrone.id as number,
        part: adjustingPart,
        type,
        value: valueSeconds,
      });
      onClose();
    },
    [adjustType, adjustingPart, hasDrone, selectedDrone.id, adjustDroneMaintenance]
  );

  useGetDroneHistoryQuery(selectedDrone.id, {
    skip: !hasDrone,
  });

  const summaryStats = React.useMemo(() => {
    if (!maintenance) {
      return [
        { label: 'Total Flight Count', value: '—' },
        { label: 'Total Flight Time', value: '—' },
        { label: 'Total Flight Distance', value: '—' },
      ];
    }
    return [
      { label: 'Total Flight Count', value: maintenance.totalFlightCount },
      { label: 'Total Flight Time', value: formatFlightTime(maintenance.totalFlightTime) },
      { label: 'Total Flight Distance', value: formatFlightDistance(maintenance.totalFlightDistance) },
    ];
  }, [maintenance]);

  const motorItems = React.useMemo((): RuntimeItem[] => {
    if (!maintenance) return [];
    return MOTOR_KEYS.map((key, i) =>
      partToRuntimeItem(`Motor ${i + 1}`, maintenance[key], 'motor')
    );
  }, [maintenance]);

  const propellerItems = React.useMemo((): RuntimeItem[] => {
    if (!maintenance) return [];
    return PROP_KEYS.map((key, i) =>
      partToRuntimeItem(`Propeller ${i + 1}`, maintenance[key], 'propeller')
    );
  }, [maintenance]);

  return (
    <ArcStack>
      <ArcStack direction="row" gap={3}>
        {summaryStats.map(({ label, value }) => (
          <StatCard key={label} label={label} value={value} />
        ))}
      </ArcStack>
      <ArcStack direction="row" gap={3} marginTop={3}>
        <RuntimeSection
          title="Motor Runtime"
          items={motorItems}
          onAdjustOptionClick={handleAdjustOptionClick}
          onReplaceClick={handleReplaceClick}
        />
        <RuntimeSection
          title="Propeller Runtime"
          items={propellerItems}
          onAdjustOptionClick={handleAdjustOptionClick}
          onReplaceClick={handleReplaceClick}
        />
      </ArcStack>
      {open && adjustType != null && (
        <AdjustPopup
          type={adjustType}
          value={adjustValue}
          open={open}
          onClose={onClose}
          onSave={handleSave}
          onValueChange={setAdjustValue}
        />
      )}
    </ArcStack>
  );
};

export default MaintenancePanel;
